import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription, filter } from 'rxjs';
import { Device } from 'src/app/model/device';
import { DeviceInfo } from 'src/app/model/device-info';
import { DeviceStatus } from 'src/app/model/device-status';
import { IOPayload } from 'src/app/model/io-payload';
import { AppState } from 'src/app/store/app.reducer';
import { selectCurrentDevice, selectCurrentDeviceInfo, selectCurrentDeviceStatus, selectDeviceInfoGatewayById, selectInputEdited, selectOutputEdited } from 'src/app/store/device/device.selector';
import { EditInputDialogComponent } from '../edit-input-dialog/edit-input.dialog.component';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { EditOutputDialogComponent } from '../edit-output-dialog/edit-output.dialog.component';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { isMtmacModel, isMtmacStandardModel, isMtmacUsModel } from 'src/app/conf/device-models';
import { Site } from 'src/app/model/site';
import { selectSites } from 'src/app/store/site/site.selector';
import { MTMACSTD_MINFWVER_PHASEIII, MTMACUS_MINFWVER_PHASEIII } from 'src/app/conf/mtma-module';
import { compareFirmwareVersions } from 'src/app/modules/shared/utils/version-compare';

@Component({
	selector: 'app-virtual-device',
	templateUrl: './virtual-device.component.html',
	providers: [ConfirmationService, DialogService],
	styles: `
		:host ::ng-deep .p-panel-header {
			border: 1px solid #D5D5D5 !important;
			border-bottom: none !important;
		}
		:host ::ng-deep .p-panel-content {
			border: 1px solid #D5D5D5 !important;
		}
	`
})
export class VirtualDeviceComponent implements OnInit, OnDestroy {
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	deviceInfo$: Observable<DeviceInfo | undefined>;
	deviceInfoSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	inputEdited: boolean = false;
	inputEdited$: Observable<boolean | undefined>;
	inputEditedSub: Subscription | undefined;
	outputEdited: boolean = false;
	outputEdited$: Observable<boolean | undefined>;
	outputEditedSub: Subscription | undefined;
	parentDeviceInfo$: Observable<DeviceInfo | undefined>;
	parentDeviceInfoSub: Subscription | undefined;

	parentDeviceInfo: DeviceInfo;
	parentDeviceModelId: number;
	owned: boolean;
	device: Device;
	inputs: IOPayload[];
	outputs: IOPayload[];
	deviceStatus: DeviceStatus;

	dialogSub: Subscription;

	currentSite: Site;
	sites: Site[];
	sites$: Observable<Site[] | undefined>;
	sitesSub: Subscription | undefined;

	loading: boolean = true;
	isMtmaIO: boolean = false;
	isMtmaPhaseIII: boolean = false;

	ref: DynamicDialogRef | undefined;

   screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void{
		this.device$ = this.store.select(selectCurrentDevice);
		this.deviceInfo$ = this.store.select(selectCurrentDeviceInfo);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.inputEdited$ = this.store.select(selectInputEdited);
		this.outputEdited$ = this.store.select(selectOutputEdited);
		this.sites$ = this.store.select(selectSites);
		this.parentDeviceInfo$ = this.store.select(selectDeviceInfoGatewayById);
	}

	ngOnInit(): void {
		this.deviceSub = this.device$.pipe(filter(d => !!d)) // currentDevice === undefined when deleting
		.subscribe(device => {
			this.owned = device.CurrentlyOwned;
			this.device = device;
		});
		this.deviceInfoSub = this.deviceInfo$.pipe(filter(di => !!di))
			.subscribe(deviceInfo => {
				this.inputs = deviceInfo.Terminals.filter(t => t.InputId !== undefined);
				this.outputs = deviceInfo.Terminals.filter(t => t.OutputId !== undefined);
			}
		);
		this.parentDeviceInfoSub = this.parentDeviceInfo$.pipe(filter(d => !!d))
			.subscribe(parentDeviceInfo => {
				// is called just if parent is a MTMA
				this.parentDeviceInfo = parentDeviceInfo;
				this.isMtmaPhaseIIICheck();
				this.loading = false;
			}
		);
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceStatus => {
			this.deviceStatus = deviceStatus;
		});
		this.inputEditedSub = this.inputEdited$?.pipe(filter(edit => !!edit))
		.subscribe((edit) => {
			this.store.dispatch(DeviceActions.updateCurrentDeviceStatus());
			if (edit) {
				this.inputEdited = true;
				setTimeout(() => {
					this.inputEdited = false;
				}, 2000);
			}
		});
		this.outputEditedSub = this.outputEdited$?.pipe(filter(edit => !!edit))
		.subscribe((edit) => {
			this.store.dispatch(DeviceActions.updateCurrentDeviceStatus());
			if (edit) {
				this.outputEdited = true;
				setTimeout(() => {
					this.outputEdited = false;
				}, 2000);
			}
		});
		this.sitesSub = this.sites$?.subscribe(
			(sites) => {
				this.sites = sites;
				this.sites.forEach(site => {
					if (this.device.SiteId === site.Id) {
						this.currentSite = site;
						this.isMtmaIO = this.isIOMtma();
						if (this.isMtmaIO) {
							this.store.dispatch(DeviceActions.getDeviceInfoGatewayByDeviceId({ deviceId: this.device.ParentId }));
						} else {
							this.loading = false;
						}
					}
				});
			}
		);

		this.onResize();
	}

	ngOnDestroy(): void {
		this.deviceSub?.unsubscribe();
		this.deviceInfoSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.inputEditedSub?.unsubscribe();
		this.outputEditedSub?.unsubscribe();
		this.sitesSub?.unsubscribe();
	}

	inputStatusLabel(input: IOPayload): string {
		return (this.deviceStatus.Inputs.get(input.InputId) !== undefined) ? this.deviceStatus.Inputs.get(input.InputId) : '0';
	}

	inputActive(input: IOPayload): boolean {
		return this.deviceStatus.Inputs.get(input.InputId) === input.StateLabelActive;
	}

	outputActive(output: IOPayload): boolean {
		return this.deviceStatus.Outputs.get(output.OutputId) === output.StateLabelActive;
	}

	isIOMtma(): boolean {
		if (this.device.ParentId !== undefined && this.device.ParentId !== null && this.currentSite !== undefined &&
			this.currentSite.Devices !== undefined && this.currentSite.Devices !== null && this.currentSite.Devices.length > 0) {
			const parentDeviceList = this.currentSite.Devices.filter(device => device.Id === this.device.ParentId);
			if (parentDeviceList.length > 0) {
				this.parentDeviceModelId = parentDeviceList[0].ModelId;
				return isMtmacModel(this.parentDeviceModelId);
			}
		}
		return false;
	}

	isMtmaPhaseIIICheck(): boolean {
		if (this.parentDeviceInfo !== undefined && this.parentDeviceInfo !== null) {
			const isMTMA: boolean = isMtmacModel(this.parentDeviceModelId);
			if (isMTMA) {
				if (isMtmacStandardModel(this.parentDeviceModelId)) {
					this.isMtmaPhaseIII = compareFirmwareVersions(this.parentDeviceInfo.FwVersion, MTMACSTD_MINFWVER_PHASEIII) >= 0;
				}
				if (isMtmacUsModel(this.parentDeviceModelId)) {
					this.isMtmaPhaseIII = compareFirmwareVersions(this.parentDeviceInfo.FwVersion, MTMACUS_MINFWVER_PHASEIII) >= 0;
				}
			}
			return isMTMA;
		}
		return false;
	}

	editInput(terminal: IOPayload): void {
		this.ref = this.dialogService.open(EditInputDialogComponent, {
			header: this.translate.instant('EDIT_MULTI_IO'),
			width: '70vw',
			breakpoints: { '1600px' : '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				terminal,
				device: this.device,
			},
		});
	}

	editOutput(terminal: IOPayload): void {
		this.ref = this.dialogService.open(EditOutputDialogComponent, {
			header: this.translate.instant('EDIT_MULTI_IO'),
			width: '70vw',
			breakpoints: { '1600px' : '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				terminal: terminal,
				device: this.device,
				isIoMtma: this.isMtmaIO,
				isMtmaPhaseIII: this.isMtmaPhaseIII
			},
		});
	}

	outputBistable(output: IOPayload): boolean {
		// eslint-disable-next-line no-bitwise
		return !!(output.CommandType & 1);
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}
